import { t } from '@lingui/macro'
import styled, { useTheme } from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { mixpanel } from '@/mixpanel'
import { Badge } from '@/modules/shared/components/badge/Badge'
import { ExternalSourceCard } from '@/modules/shared/components/source-card/external-source-card/ExternalSourceCard'
import { InternalSourceCard } from '@/modules/shared/components/source-card/internal-source-card/InternalSourceCard'
import { ScreenType, useScreenType } from '@/modules/shared/hooks/useScreenType'
import { useToast } from '@/modules/shared/hooks/useToast'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { useNavigate } from '@/router-wrapper'
import { openCozo, openUrl, openVidis } from '@/utils/external-resources'
import { useInvoiceLink } from '@/utils/navigation'

import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { ReactNode } from 'react'

type Props = {
  className?: string
  unreadFiles?: number
}

type ExternalLinkConfig = {
  title: string
  description: string
  icon: IconProp
  onClick: () => void
  badge?: ReactNode
}

export const DocumentExternalLinkSection = ({ unreadFiles, className }: Props) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const invoiceLink = useInvoiceLink()
  const { showErrorToast } = useToast()
  const isLoggedInForMandatee = useLoggedInForMandatee()
  const screenType = useScreenType()

  const externalLinks: ExternalLinkConfig[] = [
    {
      title: t({
        id: 'cozo.title',
        message: 'Medische resultaten',
      }),
      description: t({
        id: 'cozo.description',
        message: 'Bekijk je medische resultaten in CoZo.',
      }),
      icon: ['fal', 'file-medical'],
      onClick: () => {
        mixpanel.track('documents_medical_results', {
          is_mandate: isLoggedInForMandatee,
        })
        openCozo()
      },
    },
    {
      title: t({
        id: 'vidis.title',
        message: 'Voorschriften',
      }),
      description: t({
        id: 'vidis.description',
        message: 'Bekijk je voorschriften',
      }),
      icon: ['fal', 'prescription-bottle'],
      onClick: () => {
        mixpanel.track('documents_prescriptions', {
          is_mandate: isLoggedInForMandatee,
        })
        openVidis()
      },
    },
  ]

  if (invoiceLink && invoiceLink.code !== 'env_missing') {
    externalLinks.push({
      title: t({ id: 'mailtopay.title', message: 'Facturen' }),
      description: t({
        id: 'mailtopay.description',
        message: 'Bekijk en betaal je ziekenhuisfacturen.',
      }),
      icon: ['fal', 'file-invoice'],
      badge: invoiceLink.code === 'oazis_missing' ? <Badge>!</Badge> : null,
      onClick: () => {
        if (invoiceLink.url) {
          mixpanel.track('invoicing_open', {
            is_mandate: isLoggedInForMandatee,
          })
          openUrl(invoiceLink.url)
        } else {
          showErrorToast({
            message: invoiceLink.message ?? 'Er is een fout opgetreden bij het openen van de factuurpagina.',
          })
        }
      },
    })
  }

  const Container = screenType === ScreenType.Mobile ? StMobileContainer : StDesktopContainer

  return (
    <Container className={className} gap={theme.UI.Spacing.Space4}>
      {screenType === ScreenType.Mobile && (
        <InternalSourceCard
          title={t({
            id: 'documents.title',
            message: 'Documenten',
          })}
          badge={unreadFiles && unreadFiles > 0 ? <Badge>{unreadFiles >= 10 ? '9+' : unreadFiles}</Badge> : null}
          description={t({
            id: 'documents.description',
            message: 'Bekijk je persoonlijke documenten.',
          })}
          icon={['fal', 'file']}
          onClick={() => {
            navigate('/documents/personal')
          }}
        />
      )}

      {externalLinks.map((link, index) => (
        <ExternalSourceCard
          key={index}
          title={link.title}
          description={link.description}
          icon={link.icon}
          badge={link.badge}
          onClick={link.onClick}
        />
      ))}
    </Container>
  )
}

const StMobileContainer = styled(FlexColumn)``

const StDesktopContainer = styled(FlexRow)`
  > * {
    flex: 1;
  }
`
